import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FiMenu, FiX } from "react-icons/fi";
import { AiOutlineShoppingCart } from "react-icons/ai";
import Button from "../atoms/Button";
import SoundButton from "../atoms/SoundButton";
import { onLogout } from "../../redux/managers/authManager";
import logo from "../../assets/logo.png";
import arror_less from "../../assets/arrow_less.svg";

const Nav = ({ signedIn, dispatch, history, cartTotal, toggleCart }) => {
  const location = useLocation();
  const activeRoute = location.pathname;
  const isHomepage = location.pathname === "/";

  const baseNavLinks = [
    { text: "HOME", path: "/", isHash: false },
    { text: "B.E.L.A.", path: "#bela", isHash: true },
  ];

  const navLinks = isHomepage
    ? [...baseNavLinks, { text: "ABOUT", path: "#about", isHash: true }]
    : baseNavLinks;

  const loggedInLinks = [
    { text: "HQ", path: "/hq", isHash: false },
    { text: "MISSION", path: "/mission", isHash: false },
  ];

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // account dropdown state copied from nav1
  const [account, setAccount] = useState(false);
  useEffect(() => {
    // Trigger animation on mount
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  // Listen for location changes and scroll to hash if present
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      // Wait for the page to render, then scroll
      const timer = setTimeout(() => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, location.hash]);

  const handleNavClick = (e, path, isHash) => {
    if (isHash) {
      e.preventDefault();      const doScrollToHash = (hash) => {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      };      if (path === "#bela") {
        if (isHomepage) {
          doScrollToHash(path);
        } else {
          if (history && typeof history.push === "function") {
            history.push("/" + path);
          } else {
            window.location.href = "/" + path;
          }
        }
      } else {
        const element = document.querySelector(path);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    }
    setMenuOpen(false);
  };
  const handleLogout = () => {
    if (dispatch && onLogout) {
      dispatch(onLogout());
    }
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.pageYOffset || window.scrollY || 0;
      if (offset > 10) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Div>
      <StyledNav scrolled={scrolled} $isVisible={isVisible}>
        <Logo href="/" $isVisible={isVisible}>
          <img src={logo} alt="MISSION: G.A.I.A." />
        </Logo>
        <RightSection>
          <NavLinks className="desktop-nav">
            {navLinks.map((link, index) => (
              <NavLinkItem key={link.path} $delay={`${0.3 + index * 0.1}s`}>
                {link.isHash ? (
                  <StyledNavAnchor
                    href={link.path}
                    active={activeRoute === link.path ? 1 : 0}
                    onClick={(e) => handleNavClick(e, link.path, link.isHash)}
                  >
                    {link.text}
                    {activeRoute === link.path && (
                      <ActiveIndicator>
                        <Underline />
                        <Dot />
                      </ActiveIndicator>
                    )}
                  </StyledNavAnchor>
                ) : (
                  <StyledNavLink
                    to={link.path}
                    active={activeRoute === link.path ? 1 : 0}
                  >
                    {link.text}
                    {activeRoute === link.path && (
                      <ActiveIndicator>
                        <Underline />
                        <Dot />
                      </ActiveIndicator>
                    )}
                  </StyledNavLink>
                )}
              </NavLinkItem>
            ))}
            {signedIn &&
              loggedInLinks.map((link, index) => (
                <NavLinkItem key={link.path} $delay={`${0.3 + index * 0.1}s`}>
                  <StyledNavLink
                    to={link.path}
                    active={activeRoute === link.path ? 1 : 0}
                  >
                    {link.text}
                    {activeRoute === link.path && (
                      <ActiveIndicator>
                        <Underline />
                        <Dot />
                      </ActiveIndicator>
                    )}
                  </StyledNavLink>
                </NavLinkItem>
              ))}
            {!signedIn && (
              <>
                <NavLinkItem $delay="0.6s">
                  <StyledButtonLink
                    to="/login"
                    active={activeRoute === "/login" ? 1 : 0}
                  >
                    Log in
                  </StyledButtonLink>
                </NavLinkItem>
                <NavLinkItem $delay="0.7s">
                  <StyledButtonLink
                    to="/register"
                    active={activeRoute === "/register" ? 1 : 0}
                  >
                    Sign up
                  </StyledButtonLink>
                </NavLinkItem>
              </>
            )}
          </NavLinks>

          {signedIn && (
            <AccountContainer
              onMouseEnter={() => setAccount(true)}
              onMouseLeave={() => setAccount(false)}
            >
              <SoundButton>
                <ArrowContainer>
                  <img src={arror_less} alt="account arrow" />
                  <span>Account</span>
                </ArrowContainer>
              </SoundButton>

              <ButtonColumn account={account} onMouseOver={() => setAccount(true)}>
                <SoundButton>
                  <Button text="Log out" handleOnClick={handleLogout} height={35} />
                </SoundButton>
                <Link to="/profile">
                  <SoundButton>
                    <Button text="Profile" height={35} />
                  </SoundButton>
                </Link>
              </ButtonColumn>
            </AccountContainer>
          )}

          <MenuButton onClick={() => setMenuOpen(true)} menuOpen={menuOpen}>
            <FiMenu size={32} />
          </MenuButton>
        </RightSection>
      </StyledNav>

      <MobileMenuBox menuOpen={menuOpen}>
        <CloseButton onClick={() => setMenuOpen(false)}>
          <FiX size={32} />
        </CloseButton>
        <MobileNavLinks>
          {signedIn &&
            loggedInLinks.map((link) => (
              <StyledNavLink
                to={link.path}
                key={link.path}
                active={activeRoute === link.path ? 1 : 0}
                onClick={() => setMenuOpen(false)}
              >
                {link.text}
              </StyledNavLink>
            ))}
          {navLinks.map((link) =>
            link.isHash ? (
              <StyledNavAnchor
                href={link.path}
                key={link.path}
                active={activeRoute === link.path ? 1 : 0}
                onClick={(e) => handleNavClick(e, link.path, link.isHash)}
              >
                {link.text}
              </StyledNavAnchor>
            ) : (
              <StyledNavLink
                to={link.path}
                key={link.path}
                active={activeRoute === link.path ? 1 : 0}
                onClick={() => setMenuOpen(false)}
              >
                {link.text}
              </StyledNavLink>
            )
          )}
          {!signedIn && (
            <>
              <StyledButtonLink
                to="/login"
                active={activeRoute === "/login" ? 1 : 0}
                onClick={() => setMenuOpen(false)}
              >
                Log in
              </StyledButtonLink>
              <StyledButtonLink
                to="/register"
                active={activeRoute === "/register" ? 1 : 0}
                onClick={() => setMenuOpen(false)}
              >
                Sign up
              </StyledButtonLink>
            </>
          )}
          {signedIn && (
            <>
              <StyledButtonLink
                to="/profile"
                active={activeRoute === "/profile" ? 1 : 0}
                onClick={() => setMenuOpen(false)}
              >
                Profile
              </StyledButtonLink>
              <StyledButtonLink
                as="button"
                onClick={() => {
                  handleLogout();
                  setMenuOpen(false);
                }}
              >
                Log out
              </StyledButtonLink>
            </>
          )}
        </MobileNavLinks>
      </MobileMenuBox>
    </Div>
  );
};

export default Nav;

// Keyframe Animations
const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const Div = styled.div`
  z-index: 9999;
`;

const StyledNav = styled.nav`
  width: 100vw;
  min-width: 320px;
  z-index: 9998;
  height: 85px;
  background: ${({ scrolled }) =>
    scrolled
      ? "rgba(42, 40, 196, 0.4)"
      : "linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%)"};
  backdrop-filter: ${({ scrolled }) => (scrolled ? "blur(12px)" : "none")};
  transition: background 0.3s, backdrop-filter 0.3s;
  border-radius: 0px;
  display: flex;
  align-items: center;
  padding: 0 18px;
  justify-content: space-between;
  box-sizing: border-box;
  animation: ${({ $isVisible }) => ($isVisible ? slideDown : "none")} 0.6s ease-out;
  box-shadow: ${({ scrolled }) =>
    scrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none"};
  overflow: visible;
    height: 74px;
    padding: 0 24px;
  }

  @media (min-width: 768px) {
    height: 80px;
    padding: 0 32px;
  }

  @media (min-width: 900px) {
    height: 85px;
    padding: 0 40px;
  }

  @media (min-width: 1024px) {
    height: 90px;
    padding: 0 48px;
  }

  @media (min-width: 1280px) {
    height: 95px;
    padding: 0 64px;
  }

  @media (min-width: 1440px) {
    height: 100px;
    padding: 0 80px;
  }

  @media (min-width: 1600px) {
    height: 100px;
    padding: 0 100px;
  }
`;

const Logo = styled.a`
  display: flex;
  align-items: center;
  height: 20px;
  margin-right: 12px;
  animation: ${({ $isVisible }) => ($isVisible ? fadeIn : "none")} 0.8s ease-out 0.2s both;
  transition: transform 0.3s ease, opacity 0.3s ease;
  cursor: pointer;
  text-decoration: none;

  img {
    height: 100%;
    width: auto;
    object-fit: contain;
    max-width: 100%;
  }

  &:hover {
    transform: scale(1.05);
    opacity: 0.9;
  }

  @media (min-width: 480px) {
    height: 22px;
    margin-right: 14px;
  }

  @media (min-width: 640px) {
    height: 24px;
    margin-right: 16px;
  }

  @media (min-width: 768px) {
    height: 26px;
    margin-right: 18px;
  }

  @media (min-width: 900px) {
    height: 28px;
    margin-right: 20px;
  }

  @media (min-width: 1024px) {
    height: 30px;
  }

  @media (min-width: 1280px) {
    height: 32px;
  }

  @media (min-width: 1440px) {
    height: 35px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
`;

const NavLinks = styled.ul`
  display: flex;
  align-items: center;
  gap: 2px;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 767px) {
    display: none;
  }

  @media (min-width: 768px) {
    gap: 4px;
  }

  @media (min-width: 900px) {
    gap: 8px;
  }
`;

const NavLinkItem = styled.li`
  position: relative;
  animation: ${fadeInUp} 0.5s ease-out ${props => props.$delay || '0.3s'} both;
`;

const StyledNavLink = styled(Link)`
  color: #fff;
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 400;
  position: relative;
  padding: 8px 0;
  margin: 0 8px;
  transition: color 0.3s ease, transform 0.3s ease;
  display: inline-block;
  
  &:hover {
    color: #ffa726;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (min-width: 768px) {
    font-size: 0.8rem;
    margin: 0 10px;
  }

  @media (min-width: 900px) {
    font-size: 0.85rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.9rem;
    margin: 0 12px;
  }

  @media (min-width: 1280px) {
    font-size: 1rem;
    margin: 0 14px;
  }

  @media (min-width: 1440px) {
    font-size: 1.05rem;
    margin: 0 16px;
  }

  @media (min-width: 1600px) {
    font-size: 1.1rem;
  }
`;

const StyledNavAnchor = styled.a`
  color: #fff;
  text-decoration: none;
  font-size: 0.75rem;
  font-weight: 400;
  position: relative;
  padding: 8px 0;
  margin: 0 8px;
  cursor: pointer;
  transition: color 0.3s ease, transform 0.3s ease;
  display: inline-block;
  
  &:hover {
    color: #ffa726;
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }

  @media (min-width: 768px) {
    font-size: 0.8rem;
    margin: 0 10px;
  }

  @media (min-width: 900px) {
    font-size: 0.85rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.9rem;
    margin: 0 12px;
  }

  @media (min-width: 1280px) {
    font-size: 1rem;
    margin: 0 14px;
  }

  @media (min-width: 1440px) {
    font-size: 1.05rem;
    margin: 0 16px;
  }

  @media (min-width: 1600px) {
    font-size: 1.1rem;
  }
`;

const ActiveIndicator = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -10px;
  display: flex;
  align-items: center;
  gap: 2px;
  animation: ${scaleIn} 0.4s ease-out;
`;

const Underline = styled.div`
  width: 32px;
  height: 4px;
  background: #ffa726;
  border-radius: 2px;
  animation: ${scaleIn} 0.5s ease-out;
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  background: #ffa726;
  border-radius: 50%;
  animation: ${scaleIn} 0.6s ease-out;
  
  @keyframes pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.2);
    }
  }
  
  animation: ${scaleIn} 0.6s ease-out, pulse 2s ease-in-out infinite;
`;

const AuthLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const StyledButtonLink = styled(Link)`
  color: #fff;
  background: #ffa726;
  border: none;
  border-radius: 8px;
  padding: 8px 24px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  margin: 0 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  display: inline-block;
  
  &:hover {
    background: #ff9800;
    color: #fff;
    transform: translateY(-3px);
    box-shadow: 0 6px 16px rgba(255, 167, 38, 0.3);
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(255, 167, 38, 0.3);
  }
  
  @media (min-width: 1440px) {
    padding: 12px 32px;
    font-size: 1rem;
  }
`;

const ContentfulButton = styled.a`
  color: #fff;
  background: #2563eb;
  border: none;
  border-radius: 8px;
  padding: 8px 24px;
  font-size: 1rem;
  font-weight: 700;
  text-decoration: none;
  margin: 0 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
  display: inline-block;
  
  &:hover {
    background: #1d4ed8;
    color: #fff;
    transform: translateY(-3px) scale(1.05);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.5);
  }

  &:active {
    transform: translateY(-1px) scale(1.02);
    box-shadow: 0 3px 12px rgba(37, 99, 235, 0.4);
  }
  
  @media (min-width: 1440px) {
    padding: 12px 32px;
    font-size: 1rem;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  margin-left: 16px;
  transition: all 0.3s ease;
  animation: ${fadeInUp} 0.5s ease-out 0.4s both;
  
  &:hover {
    transform: scale(1.1) rotate(90deg);
    color: #ffa726;
  }

  &:active {
    transform: scale(0.95);
  }
  
  @media (max-width: 767px) {
    display: ${({ menuOpen }) => (menuOpen ? "none" : "block")};
  }
`;

const slideInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MobileMenuBox = styled.div`
  position: absolute;
  top: 80px;
  left: 0;
  width: 100vw;
  background:linear-gradient(135deg, #0f172a 0%, #1e3a8a 50%, #1e40af 100%);
  box-shadow: 0 8px 32px rgba(0,0,0,0.2);
  border-radius: 0 0 24px 24px;
  z-index: 100;
  display: ${({ menuOpen }) => (menuOpen ? "flex" : "none")};
  flex-direction: column;
  align-items: flex-end;
  animation: ${slideInDown} 0.4s ease-out;
  backdrop-filter: blur(10px);
  
  @media (min-width: 768px) {
    display: none;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  margin: 16px 24px 0 0;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1) rotate(90deg);
    color: #ffa726;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MobileNavLinks = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 32px 0 32px 0;
  
  & > * {
    animation: ${fadeInUp} 0.4s ease-out backwards;
  }
  
  & > *:nth-child(1) { animation-delay: 0.1s; }
  & > *:nth-child(2) { animation-delay: 0.15s; }
  & > *:nth-child(3) { animation-delay: 0.2s; }
  & > *:nth-child(4) { animation-delay: 0.25s; }
  & > *:nth-child(5) { animation-delay: 0.3s; }
  & > *:nth-child(6) { animation-delay: 0.35s; }
`;

/* ---- Account dropdown styles copied from nav1 (kept identical) ---- */
const AccountContainer = styled.div`
  margin: 0 32px;
  padding: 16px 0;
  cursor: pointer;
  position: relative;
`;

const ButtonColumn = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  transform: translateX(-12%);
  display: ${({ account }) => (account === true ? "flex" : "none")};
  flex-direction: column;
  background: transparent; /* nav1 used transparent background for dropdown wrapper */
  z-index: 9999;
`;

const ArrowContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  color: white;
  img {
    height: 14px;
  }
  span {
    font-size: 14px;
    letter-spacing: 1px;
  }
`;